import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  Container,
  Paper,
  Text,
  Group,
  Divider,
  Badge,
  Grid,
  Loader,
  Center,
  Button,
  Modal,
  Table,
  Badge as MantineBadge,
  TextInput,
  Tabs,
  Stack,
  Select,
  MultiSelect,
  Group as MantineGroup,
  Accordion,
  ActionIcon,
  Avatar,
  ThemeIcon,
  Progress,
} from "@mantine/core";
import {
  IconEdit,
  IconTarget,
  IconCoin,
  IconCalendar,
  IconUsers,
  IconUserPlus,
  IconLock,
  IconArrowLeft,
  IconArrowRight,
  IconTrendingUp,
  IconCheck,
  IconBuildingCommunity,
} from "@tabler/icons-react";
import { httpsCallable } from "firebase/functions";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
  query,
  limit,
  startAfter,
  endBefore,
  limitToLast,
  orderBy,
  QueryDocumentSnapshot,
  DocumentData,
  writeBatch,
  increment,
  serverTimestamp,
  collectionGroup,
  where,
} from "firebase/firestore";
import { db, functions } from "../../firebase/FirebaseCredential";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";
import { toast } from "react-toastify";
import BenchBreadcrumbs from "../UIComponent/Components/BenchBreadcrumbs";
import BenchHeading from "../pages/UI/BenchHeading";
import { ROUTES } from "../../routes/routesPaths";
import { SimpleGrid, Box } from "@mantine/core";
import { BenchTable } from "../pages/UI";
import BenchCard from "../UIComponent/Components/BenchCard";
import AssignChallengeModal from "./AssignChallengeModal";

export default function ChallengeViewPage() {
  const { challengeId } = useParams<{ challengeId: string }>();
  const [challenge, setChallenge] = useState<any>(null);
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [isChallengeAssignModalOpen, setIsChallengeAssignModalOpen] =
    useState(false);

  const [editingTeam, setEditingTeam] = useState<any>(null);
  const [newTeamName, setNewTeamName] = useState("");
  const [participants, setParticipants] = useState<any[]>([]);

  // Assign Members Modal State
  const [assignModalOpened, setAssignModalOpened] = useState(false);
  const [selectedTeamForAssign, setSelectedTeamForAssign] = useState<any>(null);
  const [availableParticipants, setAvailableParticipants] = useState<any[]>([]);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<
    string[]
  >([]);
  const [isAssigning, setIsAssigning] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") || "overview";

  // Participant Pagination State
  const [pItemsPerPage, setPItemsPerPage] = useState<string>("10");
  const [pCurrentPage, setPCurrentPage] = useState(1);
  const [pLastVisible, setPLastVisible] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [pFirstVisible, setPFirstVisible] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null);

  // Team Pagination State
  const [tItemsPerPage, setTItemsPerPage] = useState<string>("10");
  const [tCurrentPage, setTCurrentPage] = useState(1);
  const [tLastVisible, setTLastVisible] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [tFirstVisible, setTFirstVisible] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null);

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const companyId = useAppSelector(
    (state: RootState) => state.session.company_id,
  );

  const { branchesList } = useAppSelector((state: any) => state.branchList);

  const branchName = React.useMemo(() => {
    const ids: string[] = challenge?.branch_ids ||
      (challenge?.branch_id ? [challenge.branch_id] : []);
    if (ids.length === 0) return "Unknown Branch";
    if (ids.includes("all") || ids.includes("all_branches")) return "All Branches";
    return ids
      .map((id) => branchesList?.find((b: any) => b.id === id)?.name || branchesList?.find((b: any) => b.id === id)?.branch_name || id)
      .join(", ");
  }, [challenge?.branch_ids, challenge?.branch_id, branchesList]);

  const fetchParticipants = async (
    startAfterDoc: QueryDocumentSnapshot<DocumentData> | null = null,
    endBeforeDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  ) => {
    if (!companyId || !challengeId) return;
    try {
      const partsRef = collection(
        db,
        "companyprofile",
        companyId,
        "challenge_participants",
        challengeId,
        "participants",
      );

      let q = query(partsRef, orderBy("joined_at", "desc"));

      const limitVal = parseInt(pItemsPerPage);
      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc), limit(limitVal));
      } else if (endBeforeDoc) {
        q = query(q, endBefore(endBeforeDoc), limitToLast(limitVal));
      } else {
        q = query(q, limit(limitVal));
      }

      const pSnap = await getDocs(q);
      const pData = pSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setParticipants(pData);
      setPFirstVisible(pSnap.docs[0] || null);
      setPLastVisible(pSnap.docs[pSnap.docs.length - 1] || null);
    } catch (error) {
      console.error("Error fetching participants:", error);
    }
  };

  const fetchTeams = async (
    startAfterDoc: QueryDocumentSnapshot<DocumentData> | null = null,
    endBeforeDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  ) => {
    if (!companyId || !challengeId) return;
    try {
      const teamsRef = collection(
        db,
        "companyprofile",
        companyId,
        "challenge_participants",
        challengeId,
        "teams",
      );

      // Ordering by team_name as default sort
      let q = query(teamsRef, orderBy("team_name", "asc"));

      const limitVal = parseInt(tItemsPerPage);
      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc), limit(limitVal));
      } else if (endBeforeDoc) {
        q = query(q, endBefore(endBeforeDoc), limitToLast(limitVal));
      } else {
        q = query(q, limit(limitVal));
      }

      const tSnap = await getDocs(q);
      const tData = tSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTeams(tData);
      setTFirstVisible(tSnap.docs[0] || null);
      setTLastVisible(tSnap.docs[tSnap.docs.length - 1] || null);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  useEffect(() => {
    if (companyId && challengeId) {
      const fetchChallenge = async () => {
        setLoading(true);
        try {
          const docRef = doc(
            db,
            "companyprofile",
            companyId,
            "challenges",
            challengeId,
          );
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const data: any = { id: docSnap.id, ...docSnap.data() };
            setChallenge(data);

            if (data.challenge_type === "team") {
              setTCurrentPage(1);
              fetchTeams();
            }

            setPCurrentPage(1);
            fetchParticipants();
          } else {
            console.error("Challenge not found");
          }
        } catch (error) {
          console.error("Error fetching challenge:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchChallenge();
    }
  }, [companyId, challengeId]);

  useEffect(() => {
    if (companyId && challengeId && challenge) {
      fetchParticipants();
    }
  }, [pItemsPerPage]);

  useEffect(() => {
    if (companyId && challengeId && challenge?.challenge_type === "team") {
      fetchTeams();
    }
  }, [tItemsPerPage]);

  const pHandleNextPage = () => {
    if (pLastVisible && participants.length === parseInt(pItemsPerPage)) {
      setPCurrentPage(pCurrentPage + 1);
      fetchParticipants(pLastVisible, null);
    }
  };

  const pHandlePreviousPage = () => {
    if (pFirstVisible && pCurrentPage > 1) {
      setPCurrentPage(pCurrentPage - 1);
      fetchParticipants(null, pFirstVisible);
    }
  };

  const tHandleNextPage = () => {
    if (tLastVisible && teams.length === parseInt(tItemsPerPage)) {
      setTCurrentPage(tCurrentPage + 1);
      fetchTeams(tLastVisible, null);
    }
  };

  const tHandlePreviousPage = () => {
    if (tFirstVisible && tCurrentPage > 1) {
      setTCurrentPage(tCurrentPage - 1);
      fetchTeams(null, tFirstVisible);
    }
  };

  const openAssignModal = async (team: any) => {
    if (!companyId || !challengeId) return;
    setSelectedTeamForAssign(team);
    setSelectedParticipantIds([]);
    setAssignModalOpened(true);

    try {
      // 1. Fetch participants to see who already has a team
      const partsRef = collection(
        db,
        "companyprofile",
        companyId,
        "challenge_participants",
        challengeId,
        "participants",
      );
      const pSnap = await getDocs(partsRef);
      const participantsWithTeams = new Set(
        pSnap.docs.filter((d) => d.data().team_id).map((d) => d.id),
      );

      // 2. Fetch all members from the company (and branch if selected)
      const membersRef = collectionGroup(db, "members");
      let mQuery = query(membersRef, where("company_id", "==", companyId));
      const cBranchIds: string[] = challenge?.branch_ids ||
        (challenge?.branch_id ? [challenge.branch_id] : []);
      if (cBranchIds.length > 0 && !cBranchIds.includes("all_branches")) {
        mQuery = query(mQuery, where("branch_id", "in", cBranchIds));
      }
      const mSnap = await getDocs(mQuery);

      // 3. Filter members who are NOT already in a team for this challenge
      const available = mSnap.docs
        .filter((d) => !participantsWithTeams.has(d.id))
        .map((d) => {
          const data = d.data();
          const fullName = data.first_name
            ? `${data.first_name} ${data.last_name || ""}`.trim()
            : data.name || data.member_name || "Unknown Member";
          return { id: d.id, member_name: fullName, ...data };
        });

      setAvailableParticipants(available);
    } catch (error) {
      console.error("Error fetching available members:", error);
    }
  };

  const handleAssignMembers = async () => {
    if (
      !companyId ||
      !challengeId ||
      !selectedTeamForAssign ||
      selectedParticipantIds.length === 0
    )
      return;

    const teamSizeLimit = challenge?.team_size_limit || 999999;
    const currentMemberCount = selectedTeamForAssign.member_count;

    if (currentMemberCount + selectedParticipantIds.length > teamSizeLimit) {
      toast.error(
        `Cannot assign! This team has a maximum limit of ${teamSizeLimit} members.`,
      );
      return;
    }

    setIsAssigning(true);
    try {
      const batch = writeBatch(db);

      // Update or Create each selected participant with the team details
      selectedParticipantIds.forEach((pId) => {
        const pRef = doc(
          db,
          "companyprofile",
          companyId,
          "challenge_participants",
          challengeId,
          "participants",
          pId,
        );

        const memberObj = availableParticipants.find((m) => m.id === pId);

        // We use set with merge: true so if they are already a participant (but no team) it updates them,
        // and if they aren't a participant at all, it enrolls them automatically.
        batch.set(
          pRef,
          {
            member_id: pId,
            member_name: memberObj?.member_name || "Unknown Member",
            team_id: selectedTeamForAssign.id,
            team_name: selectedTeamForAssign.team_name,
            joined_at: serverTimestamp(), // Will only set if new, or overwrite if existing.
            status: "active",
          },
          { merge: true },
        );
      });

      // Update team member count
      const tRef = doc(
        db,
        "companyprofile",
        companyId,
        "challenge_participants",
        challengeId,
        "teams",
        selectedTeamForAssign.id,
      );
      batch.update(tRef, {
        member_count: increment(selectedParticipantIds.length),
      });

      await batch.commit();

      toast.success("Members successfully assigned!");
      setAssignModalOpened(false);
      fetchTeams(); // refresh teams
      fetchParticipants(); // refresh participants
    } catch (error: any) {
      toast.error("Failed to assign members: " + error.message);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const manuallyCompleteChallenge = httpsCallable(
        functions,
        "manuallyCompleteChallenge",
      );
      await manuallyCompleteChallenge({ companyId, challengeId });
      toast.success("Challenge successfully completed!");
      setChallenge((prev: any) => ({ ...prev, status: "completed" }));
      setConfirmModalOpened(false);
    } catch (error: any) {
      console.error(error);
      toast.error("Failed to complete challenge: " + error.message);
    } finally {
      setCompleting(false);
    }
  };

  const handleUpdateTeamName = async () => {
    if (!editingTeam || !newTeamName) return;
    try {
      const tRef = doc(
        db,
        "companyprofile",
        companyId,
        "challenge_participants",
        challengeId!,
        "teams",
        editingTeam.id,
      );
      await updateDoc(tRef, { team_name: newTeamName });
      setTeams(
        teams.map((t) =>
          t.id === editingTeam.id ? { ...t, team_name: newTeamName } : t,
        ),
      );
      fetchTeams();
    } catch (error) {
      console.error("Error updating team:", error);
      toast.error("Failed to update team name.");
    }
  };

  if (loading) {
    return (
      <Center style={{ height: "50vh" }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!challenge) {
    return (
      <Container size="lg" py="xl">
        <Text align="center" color="dimmed">
          Challenge not found.
        </Text>
      </Container>
    );
  }

  const breadcrumbs = [
    { label: "Challenges", path: ROUTES.CHALLENGES },
    { label: challenge.title || "View Challenge" },
  ];

  return (
    <Container size="lg" pb="xl">
      <Box
        sx={(theme) => ({
          position: "sticky",
          top: 110, // Avoid overlapping the fixed navbar
          zIndex: 100,
          backgroundColor: "#FAFAFA",
          paddingTop: theme.spacing.sm,
          marginTop: `-${theme.spacing.sm}`,
        })}
      >
        <SimpleGrid cols={2} mb="md">
          <BenchBreadcrumbs links={breadcrumbs} />

          <Group position="right">
            {(challenge.status || "").toLowerCase() !== "completed" &&
              (challenge.status || "").toLowerCase() !== "closed" && (
                <>
                  <Button onClick={() => setIsChallengeAssignModalOpen(true)}>
                    Assign Member
                  </Button>
                  <Button onClick={() => setConfirmModalOpened(true)}>
                    Complete Challenge
                  </Button>
                </>
              )}
          </Group>
        </SimpleGrid>
        <Divider my="md" />
      </Box>

      <Tabs
        value={currentTab}
        onTabChange={handleTabChange}
        keepMounted={false}
        variant="pills"
        mb="xl"
      >
        <Tabs.List
          grow
          sx={(theme) => ({
            backgroundColor: "#f1f3f5",
            borderRadius: "12px",
            padding: "4px",
            marginBottom: "20px",
            "& .mantine-Tabs-tab": {
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "14px",
              padding: "10px 18px",
              color: "#495057",
              "&[data-active]": {
                backgroundColor: "#ee374d",
                color: "white",
                boxShadow: "0 2px 8px rgba(238, 55, 61, 0.25)",
              },
            },
          })}
        >
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          {challenge.challenge_type !== "team" && (
            <Tabs.Tab value="participants">
              Participants ({participants.length})
            </Tabs.Tab>
          )}
          {challenge.challenge_type === "team" && (
            <Tabs.Tab value="teams">Teams ({teams.length})</Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="overview">
          <Stack spacing="md">
            <Grid gutter="md">
              <Grid.Col span={8}>
                <BenchCard
                  radius="lg"
                  p="xl"
                  style={{
                    border: "1px solid #ffe3e6",
                    height: "100%",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #fff0f1 100%)",
                    boxShadow: "0 4px 20px rgba(238, 55, 77, 0.05)",
                  }}
                >
                  <Group position="apart" align="flex-start" noWrap>
                    <Stack spacing={12} style={{ maxWidth: "80%" }}>
                      <Group spacing="sm">
                        <Badge
                          bg="#ee374d"
                          color="white"
                          radius="sm"
                          size="md"
                          variant="filled"
                        >
                          <Text size="xs" weight={700} transform="uppercase">
                            {challenge.challenge_type || "EVENT"} CHALLENGE
                          </Text>
                        </Badge>
                        <Badge
                          color={
                            (challenge.status || "").toLowerCase() === "active"
                              ? "green"
                              : (challenge.status || "").toLowerCase() ===
                                  "scheduled"
                                ? "yellow"
                                : (challenge.status || "").toLowerCase() ===
                                    "draft"
                                  ? "gray"
                                  : (challenge.status || "").toLowerCase() ===
                                      "completed"
                                    ? "blue"
                                    : "red"
                          }
                          variant="outline"
                          radius="sm"
                          size="md"
                        >
                          {challenge.status?.toUpperCase() || "UNKNOWN"}
                        </Badge>
                      </Group>

                      <Text
                        size="36px"
                        weight={800}
                        sx={{ color: "#1a1b1e", lineHeight: 1.2 }}
                      >
                        {challenge.title || "Unnamed Challenge"}
                      </Text>

                      <Group spacing={6} mt={-4}>
                        <IconBuildingCommunity size={16} color="#868e96" />
                        <Text size="sm" weight={600} color="dimmed">
                          {branchName}
                        </Text>
                      </Group>

                      <Text size="sm" color="dimmed" mt="sm">
                        {challenge.description || "No description provided."}
                      </Text>
                    </Stack>
                  </Group>
                </BenchCard>
              </Grid.Col>

              <Grid.Col span={4}>
                <Stack spacing="md" style={{ height: "100%" }}>
                  <BenchCard
                    radius="lg"
                    p="lg"
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Group position="apart" noWrap>
                      <div>
                        <Text
                          size="xs"
                          weight={700}
                          color="dimmed"
                          transform="uppercase"
                        >
                          Total Participants
                        </Text>
                        <Text size="28px" weight={800} color="#1a1b1e" mt={4}>
                          {participants.length}
                        </Text>
                      </div>
                      <Box
                        sx={{
                          backgroundColor: "rgba(238, 55, 77, 0.1)",
                          padding: "10px",
                          borderRadius: "50%",
                          display: "flex",
                        }}
                      >
                        <IconUsers size={20} color="#ee374d" />
                      </Box>
                    </Group>
                  </BenchCard>

                  {challenge.challenge_type === "team" && (
                    <BenchCard
                      radius="lg"
                      p="lg"
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <Group position="apart" noWrap>
                        <div>
                          <Text
                            size="xs"
                            weight={700}
                            color="dimmed"
                            transform="uppercase"
                          >
                            Total Teams
                          </Text>
                          <Text size="20px" weight={800} color="#ee374d" mt={4}>
                            {teams.length}{" "}
                            {challenge.max_teams
                              ? `/ ${challenge.max_teams}`
                              : ""}
                          </Text>
                        </div>
                        <Box
                          sx={{
                            backgroundColor: "rgba(238, 55, 77, 0.1)",
                            padding: "10px",
                            borderRadius: "50%",
                            display: "flex",
                          }}
                        >
                          <IconTarget size={20} color="#ee374d" />
                        </Box>
                      </Group>
                    </BenchCard>
                  )}
                </Stack>
              </Grid.Col>
            </Grid>
            <Grid gutter="md">
              <Grid.Col span={7}>
                <BenchCard
                  radius="lg"
                  p="lg"
                  style={{ height: "100%", border: "1px solid #f1f3f5" }}
                >
                  <Group spacing={12} mb="xl">
                    <ThemeIcon
                      size={36}
                      radius="md"
                      color="red"
                      variant="light"
                    >
                      <IconTarget size={20} />
                    </ThemeIcon>
                    <Text
                      size="sm"
                      weight={700}
                      color="dimmed"
                      transform="uppercase"
                    >
                      Target Information
                    </Text>
                  </Group>

                  <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl">
                    <div>
                      <Text
                        color="dimmed"
                        size="xs"
                        weight={600}
                        mb={6}
                        transform="uppercase"
                      >
                        Goal Type
                      </Text>
                      <Text
                        weight={700}
                        size="md"
                        style={{
                          textTransform: "capitalize",
                          color: "#1a1b1e",
                        }}
                      >
                        {challenge.goal_type || "N/A"}
                      </Text>
                    </div>
                    <div>
                      <Text
                        color="dimmed"
                        size="xs"
                        weight={600}
                        mb={6}
                        transform="uppercase"
                      >
                        Target Value
                      </Text>
                      <Text weight={700} size="md" color="#1a1b1e">
                        {challenge.target_value || "N/A"}
                      </Text>
                    </div>
                  </SimpleGrid>
                </BenchCard>
              </Grid.Col>

              <Grid.Col span={5}>
                <BenchCard
                  radius="lg"
                  p="lg"
                  style={{ height: "100%", border: "1px solid #f1f3f5" }}
                >
                  <Group spacing={12} mb="xl">
                    <ThemeIcon
                      size={36}
                      radius="md"
                      color="yellow"
                      variant="light"
                    >
                      <IconCoin size={20} />
                    </ThemeIcon>
                    <Text
                      size="sm"
                      weight={700}
                      color="dimmed"
                      transform="uppercase"
                    >
                      Rewards
                    </Text>
                  </Group>

                  <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl">
                    <div>
                      <Text
                        color="dimmed"
                        size="xs"
                        weight={600}
                        mb={6}
                        transform="uppercase"
                      >
                        Reward Type
                      </Text>
                      <Text
                        weight={700}
                        size="md"
                        style={{
                          textTransform: "capitalize",
                          color: "#1a1b1e",
                        }}
                      >
                        {challenge.reward_type === "bench_reward" ? "Bench Reward" : (challenge.reward_type || "Coins")}
                      </Text>
                    </div>
                    <div>
                      <Text
                        color="dimmed"
                        size="xs"
                        weight={600}
                        mb={6}
                        transform="uppercase"
                      >
                        Completion Coins
                      </Text>
                      <Group spacing={6}>
                        <IconCoin size={16} color="#40c057" />
                        <Text weight={800} size="md" color="green">
                          {challenge.completion_reward || 0}
                        </Text>
                      </Group>
                    </div>
                    <div>
                      <Text
                        color="dimmed"
                        size="xs"
                        weight={600}
                        mb={6}
                        transform="uppercase"
                      >
                        Participation Coins
                      </Text>
                      <Group spacing={6}>
                        <IconCoin size={16} color="#228be6" />
                        <Text weight={800} size="md" color="blue">
                          {challenge.incomplete_reward || 0}
                        </Text>
                      </Group>
                    </div>
                    {challenge.direct_reward_details && (
                      <div>
                        <Text
                          color="dimmed"
                          size="xs"
                          weight={600}
                          mb={6}
                          transform="uppercase"
                        >
                          Direct Reward
                        </Text>
                        <Text weight={700} size="md" color="#1a1b1e">
                          {challenge.direct_reward_details}
                        </Text>
                      </div>
                    )}
                  </SimpleGrid>
                </BenchCard>
              </Grid.Col>
            </Grid>
            {(challenge.challenge_type === "event" ||
              challenge.challenge_type === "team") && (
              <Grid gutter="md">
                {challenge.challenge_type === "event" && (
                  <Grid.Col span={6}>
                    <BenchCard
                      radius="lg"
                      p="lg"
                      style={{ height: "100%", border: "1px solid #f1f3f5" }}
                    >
                      <Group spacing={12} mb="xl">
                        <ThemeIcon
                          size={36}
                          radius="md"
                          color="indigo"
                          variant="light"
                        >
                          <IconCalendar size={20} />
                        </ThemeIcon>
                        <Text
                          size="sm"
                          weight={700}
                          color="dimmed"
                          transform="uppercase"
                        >
                          Timeline & Registration
                        </Text>
                      </Group>

                      <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl">
                        {challenge.registration_start && (
                          <div>
                            <Text
                              color="dimmed"
                              size="xs"
                              weight={600}
                              mb={6}
                              transform="uppercase"
                            >
                              Reg. Opens
                            </Text>
                            <Text weight={700} size="md" color="#1a1b1e">
                              {new Date(
                                challenge.registration_start,
                              ).toLocaleDateString()}
                            </Text>
                          </div>
                        )}
                        {challenge.registration_end && (
                          <div>
                            <Text
                              color="dimmed"
                              size="xs"
                              weight={600}
                              mb={6}
                              transform="uppercase"
                            >
                              Reg. Closes
                            </Text>
                            <Text weight={700} size="md" color="#1a1b1e">
                              {new Date(
                                challenge.registration_end,
                              ).toLocaleDateString()}
                            </Text>
                          </div>
                        )}
                        <div>
                          <Text
                            color="dimmed"
                            size="xs"
                            weight={600}
                            mb={6}
                            transform="uppercase"
                          >
                            Challenge Start
                          </Text>
                          <Text weight={700} size="md" color="#1a1b1e">
                            {challenge.start_date
                              ? challenge.start_date.toDate
                                ? challenge.start_date
                                    .toDate()
                                    .toLocaleDateString()
                                : new Date(
                                    challenge.start_date,
                                  ).toLocaleDateString()
                              : "TBD"}
                          </Text>
                        </div>
                        <div>
                          <Text
                            color="dimmed"
                            size="xs"
                            weight={600}
                            mb={6}
                            transform="uppercase"
                          >
                            Challenge End
                          </Text>
                          <Text weight={700} size="md" color="#1a1b1e">
                            {challenge.end_date
                              ? challenge.end_date.toDate
                                ? challenge.end_date
                                    .toDate()
                                    .toLocaleDateString()
                                : new Date(
                                    challenge.end_date,
                                  ).toLocaleDateString()
                              : "TBD"}
                          </Text>
                        </div>
                      </SimpleGrid>
                    </BenchCard>
                  </Grid.Col>
                )}

                {challenge.challenge_type === "team" && (
                  <Grid.Col span={6}>
                    <BenchCard radius="lg" p="lg" style={{ height: "100%" }}>
                      <Group spacing={8} mb="xl">
                        <IconUsers size={18} color="#ee374d" />
                        <Text
                          size="xs"
                          weight={700}
                          color="dimmed"
                          transform="uppercase"
                        >
                          Team Constraints
                        </Text>
                      </Group>

                      <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl">
                        <div>
                          <Text color="dimmed" size="xs" weight={500} mb={4}>
                            Max Teams
                          </Text>
                          <Text weight={700} size="sm">
                            {challenge.max_teams || "No limit"}
                          </Text>
                        </div>
                        <div>
                          <Text color="dimmed" size="xs" weight={500} mb={4}>
                            Team Size Limit
                          </Text>
                          <Text weight={700} size="sm">
                            {challenge.team_size_limit || "N/A"}
                          </Text>
                        </div>
                      </SimpleGrid>
                    </BenchCard>
                  </Grid.Col>
                )}

                {challenge.challenge_type === "event" && (
                  <Grid.Col span={6}>
                    <BenchCard radius="lg" p="lg" style={{ height: "100%" }}>
                      <Group spacing={8} mb="xl">
                        <IconLock size={18} color="#ee374d" />
                        <Text
                          size="xs"
                          weight={700}
                          color="dimmed"
                          transform="uppercase"
                        >
                          Constraints
                        </Text>
                      </Group>

                      <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl">
                        <div>
                          <Text color="dimmed" size="xs" weight={500} mb={4}>
                            Member Limit
                          </Text>
                          <Text weight={700} size="sm">
                            {challenge.member_limit || "No limit"}
                          </Text>
                        </div>
                      </SimpleGrid>
                    </BenchCard>
                  </Grid.Col>
                )}
              </Grid>
            )}
          </Stack>
        </Tabs.Panel>

        {challenge.challenge_type !== "team" && (
          <Tabs.Panel value="participants">
            <BenchTable>
              <thead>
                <tr>
                  <th>Member Name</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Joined Date</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p) => (
                  <tr key={p.id}>
                    <td>{p.member_name}</td>
                    <td>
                      <MantineBadge
                        color={p.status === "completed" ? "green" : "blue"}
                      >
                        {p.status}
                      </MantineBadge>
                    </td>
                    <td>{p.progress || 0}</td>
                    <td>
                      {p.joined_at
                        ? new Date(
                            p.joined_at?.seconds * 1000,
                          ).toLocaleDateString()
                        : "N/A"}
                    </td>
                  </tr>
                ))}
                {participants.length === 0 && (
                  <tr>
                    <td colSpan={4} align="center">
                      No participants found.
                    </td>
                  </tr>
                )}
              </tbody>
            </BenchTable>

            <div
              style={{
                marginTop: "1rem",
                display: "flex",
                justifyContent: "space-between",
                paddingBottom: "2rem",
                marginBottom: "2rem",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Text>Rows per page : </Text>
                <Select
                  style={{ width: "100px", marginLeft: "5px" }}
                  value={pItemsPerPage}
                  onChange={(val) => setPItemsPerPage(val || "10")}
                  data={[
                    { value: "10", label: "10" },
                    { value: "25", label: "25" },
                    { value: "50", label: "50" },
                    { value: "100", label: "100" },
                  ]}
                />
              </div>
              <div style={{ alignSelf: "flex-end" }}>
                <Group>
                  <Button
                    leftIcon={<IconArrowLeft size={14} />}
                    variant="default"
                    onClick={pHandlePreviousPage}
                    disabled={pCurrentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    rightIcon={<IconArrowRight size={14} />}
                    variant="default"
                    onClick={pHandleNextPage}
                    disabled={participants.length < parseInt(pItemsPerPage)}
                  >
                    Next
                  </Button>
                </Group>
              </div>
            </div>
          </Tabs.Panel>
        )}

        {challenge.challenge_type === "team" && (
          <Tabs.Panel value="teams">
            <BenchCard>
              {teams.length === 0 ? (
                <Text align="center" my="xl" color="dimmed">
                  No teams found.
                </Text>
              ) : (
                <Accordion variant="separated" radius="md">
                  {teams.map((t) => {
                    const teamMembers = participants.filter(
                      (p) => p.team_id === t.id,
                    );
                    return (
                      <Accordion.Item key={t.id} value={t.id}>
                        <Accordion.Control>
                          <Group
                            position="apart"
                            style={{ width: "100%", paddingRight: "1rem" }}
                          >
                            <Group>
                              <Text weight={600}>{t.team_name}</Text>
                              <MantineBadge
                                color={
                                  t.status === "completed" ? "green" : "blue"
                                }
                              >
                                {t.status || "active"}
                              </MantineBadge>
                            </Group>
                            <Group spacing="lg">
                              <Text size="sm" color="dimmed">
                                Members: {t.member_count || 0}
                              </Text>
                              <Text size="sm" color="dimmed">
                                Progress: {t.total_progress || 0}
                              </Text>
                              <Group
                                spacing="xs"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button
                                  variant="subtle"
                                  size="xs"
                                  onClick={() => {
                                    setEditingTeam(t);
                                    setNewTeamName(t.team_name);
                                  }}
                                >
                                  <IconEdit size={16} />
                                </Button>
                                {/* <Button
                                  variant="subtle"
                                  color="green"
                                  size="xs"
                                  onClick={() => openAssignModal(t)}
                                >
                                  <IconUserPlus size={16} />
                                </Button> */}
                              </Group>
                            </Group>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          {teamMembers.length === 0 ? (
                            <Text
                              color="dimmed"
                              size="sm"
                              align="center"
                              my="md"
                            >
                              No members assigned to this team.
                            </Text>
                          ) : (
                            <BenchTable>
                              <thead>
                                <tr>
                                  <th>Participant</th>
                                  <th>Status</th>
                                  <th>Joined</th>
                                </tr>
                              </thead>
                              <tbody>
                                {teamMembers.map((m) => {
                                  const mName = m.member_name || "Unknown";
                                  return (
                                    <tr key={m.id}>
                                      <td>
                                        <Group spacing="sm">
                                          <Avatar
                                            color="cyan"
                                            radius="xl"
                                            size="sm"
                                          >
                                            {mName.charAt(0).toUpperCase()}
                                          </Avatar>
                                          <Text weight={500} size="sm">
                                            {mName}
                                          </Text>
                                        </Group>
                                      </td>
                                      <td>
                                        <MantineBadge
                                          size="xs"
                                          color="green"
                                          variant="light"
                                        >
                                          {m.status || "active"}
                                        </MantineBadge>
                                      </td>
                                      <td>
                                        <Text size="sm" color="dimmed">
                                          {m.joined_at?.toDate
                                            ? new Date(
                                                m.joined_at.toDate(),
                                              ).toLocaleDateString()
                                            : "N/A"}
                                        </Text>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </BenchTable>
                          )}
                        </Accordion.Panel>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              )}
            </BenchCard>{" "}
            <div
              style={{
                marginTop: "1rem",
                display: "flex",
                justifyContent: "space-between",
                paddingBottom: "2rem",
                marginBottom: "2rem",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Text>Rows per page : </Text>
                <Select
                  style={{ width: "100px", marginLeft: "5px" }}
                  value={tItemsPerPage}
                  onChange={(val) => setTItemsPerPage(val || "10")}
                  data={[
                    { value: "10", label: "10" },
                    { value: "25", label: "25" },
                    { value: "50", label: "50" },
                    { value: "100", label: "100" },
                  ]}
                />
              </div>
              <div style={{ alignSelf: "flex-end" }}>
                <Group>
                  <Button
                    leftIcon={<IconArrowLeft size={14} />}
                    variant="default"
                    onClick={tHandlePreviousPage}
                    disabled={tCurrentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    rightIcon={<IconArrowRight size={14} />}
                    variant="default"
                    onClick={tHandleNextPage}
                    disabled={teams.length < parseInt(tItemsPerPage)}
                  >
                    Next
                  </Button>
                </Group>
              </div>
            </div>{" "}
          </Tabs.Panel>
        )}
      </Tabs>

      <Modal
        opened={confirmModalOpened}
        onClose={() => setConfirmModalOpened(false)}
        title="Complete Challenge"
        centered
      >
        <Text size="sm" mb="lg">
          Are you sure you want to mark this challenge as completed? This will
          process all rewards and cannot be undone.
        </Text>
        <Group position="right">
          <Button
            variant="outline"
            onClick={() => setConfirmModalOpened(false)}
          >
            Cancel
          </Button>
          <Button color="red" onClick={handleComplete} loading={completing}>
            Confirm Completion
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={assignModalOpened}
        onClose={() => setAssignModalOpened(false)}
        title={
          <Text weight={600}>
            Assign Members to {selectedTeamForAssign?.team_name}
          </Text>
        }
        centered
        size="lg"
      >
        <Stack spacing="md">
          <Text size="sm" color="dimmed">
            Select participants from the list below to assign them to this team.
            Only participants who are not already in a team are shown.
          </Text>

          <MultiSelect
            data={availableParticipants.map((p) => ({
              value: p.id,
              label: p.member_name,
            }))}
            value={selectedParticipantIds}
            onChange={setSelectedParticipantIds}
            placeholder="Select members"
            searchable
            nothingFound="No unassigned members available"
            clearable
            maxDropdownHeight={300}
            withinPortal
          />

          <Group position="right" mt="md">
            <Button
              variant="outline"
              onClick={() => setAssignModalOpened(false)}
              disabled={isAssigning}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignMembers}
              loading={isAssigning}
              disabled={selectedParticipantIds.length === 0}
            >
              Assign Members
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!editingTeam}
        onClose={() => setEditingTeam(null)}
        title="Rename Team"
      >
        <TextInput
          label="Team Name"
          value={newTeamName}
          onChange={(e) => setNewTeamName(e.currentTarget.value)}
          mb="md"
        />
        <Button fullWidth onClick={handleUpdateTeamName} mt="md">
          Save
        </Button>
      </Modal>

      {isChallengeAssignModalOpen && (
        <AssignChallengeModal
          opened={isChallengeAssignModalOpen}
          onClose={() => setIsChallengeAssignModalOpen(false)}
          challenge={challenge}
        />
      )}
    </Container>
  );
}
